import { useState } from 'react'
import { List } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatPrice, type ProductionModels, type ProviderInfo } from '@/lib/model-lab'

const CUSTOM = '__custom__'

interface Props {
  readonly provider: ProviderInfo | undefined
  readonly model: string
  readonly productionModels?: ProductionModels
  readonly onChange: (model: string) => void
}

/**
 * Every catalog model for the provider in a dropdown (with list price and a
 * "production" tag), plus "Custom model ID…" for ids newer than the catalog.
 * Remount with a provider-specific `key` so switching provider resets the mode.
 */
export function ModelPicker({ provider, model, productionModels, onChange }: Props) {
  const catalog = provider?.models ?? []
  const [custom, setCustom] = useState(() => model !== '' && !catalog.some((m) => m.id === model))

  const productionTag = (id: string) => {
    const uses = [productionModels?.analysis === id && 'analysis', productionModels?.sms === id && 'SMS'].filter(Boolean)
    return uses.length > 0 ? `production ${uses.join(' + ')}` : null
  }

  if (custom) {
    return (
      <div className="flex gap-1">
        <Input
          autoFocus
          className="h-8 font-mono text-xs"
          value={model}
          placeholder="exact model id"
          onChange={(e) => onChange(e.target.value.trim())}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          title="Back to the model list"
          onClick={() => {
            setCustom(false)
            onChange(catalog[0]?.id ?? '')
          }}
        >
          <List className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <Select
      value={model}
      onValueChange={(value) => {
        if (value === CUSTOM) {
          setCustom(true)
          onChange('')
        } else {
          onChange(value)
        }
      }}
    >
      <SelectTrigger className="h-8 font-mono text-xs">
        {/* Explicit child: show just the id, not the two-line item content. */}
        <SelectValue placeholder="Choose a model">{model || undefined}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {catalog.map((m) => {
          const tag = productionTag(m.id)
          return (
            <SelectItem key={m.id} value={m.id}>
              <span className="flex flex-col">
                <span className="font-mono text-xs">
                  {m.id}
                  {tag && <span className="ml-2 rounded bg-emerald-100 px-1 py-px text-[10px] font-sans text-emerald-800">{tag}</span>}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {m.label} · {formatPrice(m.price)}
                </span>
              </span>
            </SelectItem>
          )
        })}
        <SelectItem value={CUSTOM}>
          <span className="text-xs italic">Custom model ID…</span>
        </SelectItem>
      </SelectContent>
    </Select>
  )
}
